<template>
  <div class="user-transactions-list">
    <div class="user-transactions__filters-wrp">
      <div class="app-list-filters">
        <input-field
          class="app-list-filters__field user-transctions__field"
          v-model.trim="filters.parentId"
          :label="'user-details.lbl-parentId' | globalize"
        />
        <select-field
          class="user-transctions__field app-list-filters__field"
          :label="'user-details.lbl-actions' | globalize"
          v-model="filters.action"
        >
          <option
            v-for="(item, Id) in actions"
            :key="`${item}-${Id}`"
            :value="item"
          >
            {{ item }}
          </option>
        </select-field>
        <select-field
          class="user-transctions__field app-list-filters__field"
          :label="'user-details.lbl-statuses' | globalize"
          v-model="filters.status"
        >
          <option
            v-for="(item, Id) in statuses"
            :key="`${item}-${Id}`"
            :value="item"
          >
            {{ item }}
          </option>
        </select-field>
        <button
          v-ripple
          class="
            user-transactions__btn
            app__button-raised
            app__button-raised--danger"
          @click="resetPermissionJobs"
        >
          {{ 'user-nft-transactions.btn-reset-permissions' | globalize }}
        </button>
        <button
          v-ripple
          class="
            user-transactions__btn
            app__button-raised"
          @click="reloadList"
        >
          {{ 'user-nft-transactions.btn-refresh' | globalize }}
        </button>
      </div>
    </div>

    <a-table
      size="small"
      :data-source="transactionsList"
      :row-key="record=>record.id"
      :pagination="false"
      :columns="columns">
      <template
        slot="address-column"
        slot-scope="address, item">
        <copyable :copy-text="item.address">
          <eth-link
            :value="item.address"
            type="address"
            :explorer-url="item.explorerUrl"
          />
        </copyable>
      </template>
      <template
        slot="url-column"
        slot-scope="txHash, item">
        <copyable :copy-text="item.txHash">
          <eth-link
            :value="item.txHash"
            type="tx"
            :explorer-url="item.explorerUrl"
          />
        </copyable>
      </template>
      <template
        slot="createdAt"
        slot-scope="text">
        {{ text | formatDateDMYT }}
      </template>
      <template
        slot="actions"
        slot-scope="item">
        <button
          v-ripple
          class="
            user-transactions__btn
            app__button-raised
            app__button-raised--warning"
          @click="resetPermissionJob(item.id)"
        >
          {{ 'user-nft-transactions.btn-reset-job' | globalize }}
        </button>
      </template>
    </a-table>
    <div class="app__more-btn-wrp">
      <collection-loader
        class="app__more-btn-wrp"
        :first-page-loader="getTransactions"
        @first-page-load="setList"
        @next-page-load="concatList"
        ref="collectionLoaderBtn"
      />
    </div>
  </div>
</template>

<script>
import { globalize } from '@/vue/filters/globalize'
import SelectField from '@/vue/fields/SelectField'
import InputField from '@/vue/fields/InputField'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import { api } from '@/api'
import { NftTransactionRecord } from '@/js/records/entities/nft-transaction.record'
import EthLink from '@/vue/common/eth-link'
import Copyable from '@/vue/common/copyable/Copyable'
import { Bus } from '@/js/helpers/event-bus'
import { shortener } from '@/js/helpers/string'
import { etherscanUrl } from '@/js/helpers/urls'
import ATable from 'ant-design-vue/lib/table'
import CollectionLoader from '@/vue/common/CollectionLoader'

const columns = ctx => {
  /* eslint-disable-next-line */
  const h = ctx.$createElement
  return [
    {
      dataIndex: 'id',
      key: 'id',
      title: globalize('user-nft-transactions.lbl-id'),
    },
    {
      dataIndex: 'userId',
      key: 'userId',
      title: globalize('user-nft-transactions.lbl-userId'),
    },
    {
      dataIndex: 'address',
      key: 'address',
      title: globalize('user-nft-transactions.lbl-address'),
      scopedSlots: { customRender: 'address-column' },
    },
    {
      dataIndex: 'action',
      key: 'action',
      title: globalize('user-nft-transactions.lbl-action'),
    },
    {
      dataIndex: 'role',
      key: 'role',
      title: globalize('user-nft-transactions.lbl-role'),
    },
    {
      dataIndex: 'status',
      key: 'status',
      title: globalize('user-nft-transactions.lbl-status'),
    },
    {
      dataIndex: 'retryCount',
      key: 'retryCount',
      title: globalize('user-nft-transactions.lbl-retryCount'),
    },
    {
      dataIndex: 'txHash',
      key: 'txHash',
      title: globalize('user-nft-transactions.lbl-txHash'),
      scopedSlots: { customRender: 'url-column' },
    },
    {
      dataIndex: 'reason',
      key: 'reason',
      title: globalize('user-nft-transactions.lbl-reason'),
    },
    {
      dataIndex: 'chain',
      key: 'chain',
      title: globalize('user-nft-transactions.lbl-chain'),
      customRender: (str, record) => {
        return `${record.chainName}`
      },
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      title: globalize('user-nft-transactions.lbl-createdAt'),
      scopedSlots: { customRender: 'createdAt' },
    },
    {
      key: 'actions',
      title: globalize('user-nft-transactions.lbl-actions'),
      scopedSlots: { customRender: 'actions' },
    },
  ]
}

export default {
  components: {
    SelectField,
    CollectionLoader,
    'copyable': Copyable,
    EthLink,
    ATable,
    InputField,
  },
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
  },
  data () {
    return {
      isPending: false,
      isLoaded: false,
      isFailed: false,
      transactionsList: [],
      statuses: ['not_started', 'pending', 'to_retry', 'failed', 'done'],
      actions: ['assign', 'revoke'],
      filters: {
        status: '',
        action: '',
        parentId: '',
      },
    }
  },
  computed: {
    columns () {
      const result = columns(this)

      return result
    },
  },
  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
  },

  async created () {
    await this.getTransactions()
  },

  methods: {
    async resetPermissionJobs () {
      try {
        await api.postWithSignature('/dev/reset_permission_jobs?user_id=' + this.id, '{}')
        Bus.success('user-nft-transactions.success-permission-reset-message')
        this.reloadList()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },
    async resetPermissionJob (jobID) {
      try {
        await api.postWithSignature('/dev/reset_permission_job?job_id=' + jobID, '{}')
        Bus.success('user-nft-transactions.success-job-reset-message')
        this.reloadList()
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },
    async getTransactions () {
      this.isLoaded = false
      this.isFailed = false

      let response = {}
      try {
        const query = {
          filter: clearObject({
            user_id: this.id,
            status: this.filters.status,
            action: this.filters.action,
            parent_id: this.filters.parentId,
          }),
        }

        response = await api.getWithSignature('/transactions', query)

        this.transactionsList = response.data.map(i => new NftTransactionRecord(i))

        return response
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      } finally {
        this.isLoaded = true
        this.isPending = false
      }
    },

    reloadList () {
      this.$refs.collectionLoaderBtn.loadFirstPage()
    },

    setList (data) {
      this.transactionsList = data.map((i) => new NftTransactionRecord(i))
    },

    concatList (data) {
      this.transactionsList = this.transactionsList.concat(
        data.map((i) => new NftTransactionRecord(i))
      )
    },
    shortener,
    etherscanUrl,
  },
}
</script>

<style lang="scss" scoped>
@import '~ant-design-vue/lib/table/style/index.css';

.user-transctions__list-wrp {
  margin-top: 4rem;
}

.user-transctions__field {
  min-width: 16.5rem;
}

.user-transactions__btn {
  min-width: 16.5rem;
}
</style>
